/**
 * Reporting & Dashboards API client. Thin wrappers over the shared `apiFetch`
 * helper (see ./api) that map to the Elysia /reports module.
 *
 * CSV exports are NOT JSON, so they bypass apiFetch and stream the response as a
 * Blob via the same same-origin `/api` base + credentials, then trigger a
 * browser download.
 */
import type {
  AttendanceReportDto,
  DashboardSummaryDto,
  GroupsReportDto,
  NewSavedReportInput,
  PeopleReportDto,
  ReportQuery,
  SavedReportDto,
  UpdateSavedReportInput,
} from '@fc/core'
import { apiFetch } from './api'

export type {
  AttendanceReportDto,
  AttendanceRowDto,
  CategoryCountDto,
  DashboardMetricDto,
  DashboardSummaryDto,
  GroupHealthRowDto,
  GroupsReportDto,
  PeopleReportDto,
  RecentCheckinDto,
  SavedReportDto,
  SavedReportKind,
  TimeBucketDto,
  UpcomingEventDto,
} from '@fc/core'

const BASE = '/api'

function qs(params: Record<string, string | number | null | undefined>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

// ---- Dashboard ----
export const getDashboard = () => apiFetch<DashboardSummaryDto>('/reports/dashboard')

// ---- List reports (JSON) ----
export const getPeopleReport = (q: ReportQuery = {}) =>
  apiFetch<PeopleReportDto>(`/reports/people${qs({ siteId: q.siteId, months: q.months })}`)

export const getAttendanceReport = (q: ReportQuery = {}) =>
  apiFetch<AttendanceReportDto>(
    `/reports/attendance${qs({ siteId: q.siteId, from: q.from, to: q.to })}`,
  )

export const getGroupsReport = (q: ReportQuery = {}) =>
  apiFetch<GroupsReportDto>(`/reports/groups${qs({ siteId: q.siteId })}`)

// ---- CSV export (Blob download) ----
async function downloadCsv(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { Accept: 'text/csv' },
  })
  if (!res.ok) throw new Error(`Export failed (${res.status})`)
  const blob = await res.blob()
  // Prefer the server-provided filename from Content-Disposition.
  const disp = res.headers.get('content-disposition') ?? ''
  const match = /filename="?([^"]+)"?/.exec(disp)
  const filename = match?.[1] ?? fallbackName
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export const exportPeopleCsv = (q: ReportQuery = {}) =>
  downloadCsv(
    `/reports/people${qs({ format: 'csv', siteId: q.siteId, months: q.months })}`,
    'people-report.csv',
  )

export const exportAttendanceCsv = (q: ReportQuery = {}) =>
  downloadCsv(
    `/reports/attendance${qs({ format: 'csv', siteId: q.siteId, from: q.from, to: q.to })}`,
    'attendance-report.csv',
  )

export const exportGroupsCsv = (q: ReportQuery = {}) =>
  downloadCsv(`/reports/groups${qs({ format: 'csv', siteId: q.siteId })}`, 'groups-report.csv')

// ---- Saved reports ----
export const listSavedReports = () => apiFetch<SavedReportDto[]>('/reports/saved')

export const createSavedReport = (input: NewSavedReportInput) =>
  apiFetch<SavedReportDto>('/reports/saved', { method: 'POST', body: JSON.stringify(input) })

export const updateSavedReport = (id: string, input: UpdateSavedReportInput) =>
  apiFetch<SavedReportDto>(`/reports/saved/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

export const deleteSavedReport = (id: string) =>
  apiFetch<void>(`/reports/saved/${id}`, { method: 'DELETE' })
